/**
 * InvitePanel.tsx
 *
 * Admin-only panel for the Invites sub-tab in the Admin section.
 * Lets admins send invite links, see pending/used invites, and copy
 * invite URLs to share manually if email isn't configured.
 */

import React, { useEffect, useState, useCallback } from 'react';

interface InvitePanelProps {
  token: string;
}

interface Invite {
  id: number;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  used: boolean;
  expired: boolean;
}

const API = 'https://coast-terminal-production.up.railway.app';

const FRONTEND_URL = 'https://coast-res.netlify.app';

const InvitePanel: React.FC<InvitePanelProps> = ({ token }) => {
  // Palette matches Dashboard.tsx (cream + navy + gold) so this panel
  // blends with the rest of the admin area instead of looking like a
  // dark pop-out. Previous dark-navy tokens are kept in comments for
  // reference in case we need a dark variant later.
  const gold = '#C9A96E';
  const goldDim = 'rgba(201,169,110,0.18)';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const blue = '#1A5A8A';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#f2ede6';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('analyst');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [newInviteToken, setNewInviteToken] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await fetch(`${API}/firms/invite-list`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setInvites(data.invites);
    } catch {
      setError('Failed to load invites.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError('');
    setSuccess('');
    setNewInviteToken(null);
    try {
      const res = await fetch(`${API}/firms/invite`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send invite');
      setSuccess(`Invite sent to ${email.trim()}.`);
      setNewInviteToken(data.token);
      setEmail('');
      await fetchInvites();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const copyLink = (inviteToken: string, id: number) => {
    const url = `${FRONTEND_URL}/accept-invite/${inviteToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const relativeTime = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const inp: React.CSSProperties = {
    backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, borderRadius: '2px',
    padding: '10px 14px', color: textPrimary, fontSize: '13px', outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box' as const,
  };

  const btnGold: React.CSSProperties = {
    backgroundColor: gold, color: bg, border: 'none', borderRadius: '2px',
    padding: '10px 24px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  };

  const pendingInvites = invites.filter(i => !i.used && !i.expired);
  const usedInvites = invites.filter(i => i.used);
  const expiredInvites = invites.filter(i => !i.used && i.expired);

  const statusColor = (invite: Invite) =>
    invite.used ? green : invite.expired ? textMuted : gold;
  const statusLabel = (invite: Invite) =>
    invite.used ? 'Accepted' : invite.expired ? 'Expired' : 'Pending';

  return (
    <div>
      <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>
        Access Control
      </div>
      <div style={{ fontSize: '22px', fontWeight: 300, color: textPrimary, fontFamily: serifFont, marginBottom: '4px' }}>
        Invite Users
      </div>
      <div style={{ fontSize: '12px', color: textMuted, marginBottom: '28px' }}>
        Send secure invite links. Links expire after 72 hours and can only be used once.
      </div>

      {/* Send invite form */}
      <form onSubmit={handleSend} style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '10px', color: textSecondary, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '16px' }}>
          New Invite
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Email</div>
            <input
              style={{ ...inp, width: '100%' }}
              type="email"
              placeholder="analyst@yourfirm.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div style={{ width: '140px' }}>
            <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>Role</div>
            <select value={role} onChange={e => setRole(e.target.value)} style={{ ...inp, width: '100%', cursor: 'pointer' }}>
              <option value="analyst">Analyst</option>
              <option value="reviewer">Reviewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="submit" disabled={sending || !email.trim()} style={{ ...btnGold, opacity: sending || !email.trim() ? 0.4 : 1, cursor: sending || !email.trim() ? 'default' : 'pointer' }}>
            {sending ? 'Sending...' : 'Send Invite'}
          </button>
        </div>

        {error && <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: 'rgba(204,34,0,0.08)', border: `1px solid rgba(204,34,0,0.25)`, color: red, fontSize: '12px' }}>{error}</div>}
        {success && (
          <div style={{ marginTop: '12px', padding: '12px 14px', backgroundColor: 'rgba(26,122,74,0.08)', border: `1px solid rgba(26,122,74,0.25)` }}>
            <div style={{ fontSize: '12px', color: green, marginBottom: newInviteToken ? '8px' : '0' }}>{success}</div>
            {newInviteToken && (
              <div>
                <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 }}>
                  Copy this invite link to share manually:
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1, backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, padding: '8px 12px', fontSize: '11px', color: textSecondary, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {`${FRONTEND_URL}/accept-invite/${newInviteToken}`}
                  </div>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(`${FRONTEND_URL}/accept-invite/${newInviteToken}`); }} style={{ backgroundColor: goldDim, border: `1px solid rgba(201,169,110,0.3)`, color: gold, padding: '8px 14px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Copy Link
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </form>

      {/* Invite list */}
      {loading ? (
        <div style={{ color: textMuted, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Loading...</div>
      ) : invites.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: textMuted, fontSize: '12px', border: `1px solid ${border}`, backgroundColor: surface }}>
          No invites sent yet.
        </div>
      ) : (
        <div style={{ backgroundColor: surface, border: `1px solid ${border}` }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, backgroundColor: surfaceRaised, display: 'flex', gap: '24px' }}>
            {[['Pending', pendingInvites.length, gold], ['Accepted', usedInvites.length, green], ['Expired', expiredInvites.length, textMuted]].map(([label, count, color]) => (
              <div key={label as string} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: color as string }}>{count}</div>
                <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
              </div>
            ))}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: surfaceRaised }}>
                {['Email', 'Role', 'Invited By', 'Sent', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invites.map(invite => (
                <tr key={invite.id} style={{ borderBottom: `1px solid ${border}`, opacity: invite.expired ? 0.5 : 1 }}>
                  <td style={{ padding: '12px 14px', color: textPrimary, fontWeight: 600 }}>{invite.email}</td>
                  <td style={{ padding: '12px 14px', color: textSecondary, textTransform: 'uppercase', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px' }}>{invite.role}</td>
                  <td style={{ padding: '12px 14px', color: textSecondary }}>{invite.invited_by}</td>
                  <td style={{ padding: '12px 14px', color: textMuted, fontSize: '11px' }}>{relativeTime(invite.created_at)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ fontSize: '9px', fontWeight: 800, color: statusColor(invite), letterSpacing: '1px', textTransform: 'uppercase' }}>
                      {statusLabel(invite)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    {!invite.used && !invite.expired && (
                      <button
                        onClick={() => copyLink(invite.id.toString(), invite.id)}
                        style={{ backgroundColor: 'transparent', border: `1px solid ${borderLight}`, color: copiedId === invite.id ? green : textSecondary, padding: '4px 10px', fontSize: '10px', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.5px' }}
                      >
                        {copiedId === invite.id ? '✓ Copied' : 'Copy Link'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitePanel;